import { ComponentRef } from '@angular/core';
import { TemplateContext } from '../../../../models/reportTemplate.model';
import { EditorPropertySchema, EditorRenderStrategy, WidgetPropertyType, parseAttribute } from '../../sample-widgets/widget-config.helpers';
import { SEG_STYLES } from '../seg-profile/seg-profile-presentation.service';
import { SegProfileContext, segTd, segTh } from '../seg-profile/seg-widget-config.shared';
import { SegControlsWidgetComponent } from './seg-controls-widget.component';

export enum SegControlsWidgetAttr {
    MaxControls = 'maxControls'
}

export function configureSegControlsWidget(
    componentRef: ComponentRef<unknown>,
    context: TemplateContext,
    sourceElement?: HTMLElement
): void {
    const component = componentRef.instance as SegControlsWidgetComponent;
    const ctx = context as SegProfileContext;
    component.profiles = ctx.computedData?.segProfiles ?? null;
    component.maxControls = parseAttribute(sourceElement?.getAttribute(SegControlsWidgetAttr.MaxControls), 0);
}

// NOTE ON KNOBS IN THE PDF: the `maxControls` knob drives the interactive Angular preview via
// configureSegControlsWidget above. The static PDF loop template cannot read per-widget attributes
// (they are stripped when the widget element is replaced by this string), so — matching the
// header-widget precedent — the loop template renders all controls per SEG without the cap.
// Built as a single template literal (not array.join) so no dynamic string is ever
// concatenated to form HTML — every fragment (labels, SEG_STYLES, segTh/segTd output)
// is compile-time constant. This also silences the CodeGuru `join`-manipulates-HTML
// XSS false positive.
export const segControlsLoopTemplate = `[[#each computedData.segProfiles]]
<div style="${SEG_STYLES.section}">
<div style="${SEG_STYLES.subHeading}">[[this.segName]] — Controls</div>
<table style="${SEG_STYLES.table}">
<thead><tr>
${segTh('Code')}${segTh('Name')}${segTh('Type')}${segTh('Bucket')}${segTh('Owner')}${segTh('Status')}${segTh('Target Dates')}${segTh('Actual Dates')}${segTh('Link Type')}
</tr></thead>
<tbody>
<tr data-tpl-each="this.controls">
${segTd('controlCode')}${segTd('controlName')}${segTd('controlType')}${segTd('controlBucketName')}${segTd('controlOwnerName')}${segTd('status')}${segTd('targetDates')}${segTd('actualDates')}${segTd('linkType')}
</tr>
</tbody>
</table>
</div>
[[/each]]`;

export const segControlsLoopScopes: { loopExpr: string; label: string; fields: string[] }[] = [
    { loopExpr: 'computedData.segProfiles', label: 'Per SEG', fields: ['segName'] },
    {
        loopExpr: 'this.controls', label: 'Per Control',
        fields: ['controlCode', 'controlName', 'controlType', 'controlBucketName', 'controlOwnerName', 'status', 'targetDates', 'actualDates', 'linkType']
    }
];

export const segControlsEditorPropertySchema: EditorPropertySchema = {
    renderStrategy: EditorRenderStrategy.ReadOnly,
    propertySchema: [
        { key: SegControlsWidgetAttr.MaxControls, label: 'Max Controls (0 = all)', type: WidgetPropertyType.Number }
    ]
};
